import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChatError,
  attachmentUrl,
  getConversation,
  sendChat,
  sendFeedback,
  streamChat,
} from '../assistant/chat-api';
import type { StepEnd, StepEvent } from '../assistant/chat-api';
import type {
  ChatAttachment,
  ChatMessage,
  ConversationDetail,
  FeedbackReason,
  MessageFeedback,
  PendingAttachment,
  ToolCall,
  TurnMeta,
  TurnRequest,
} from '../assistant/types';

let seq = 0;
function nextId(): string {
  seq += 1;
  return `m${Date.now().toString(36)}${seq}`;
}

/**
 * The conversation to reopen after a reload. Only an id: the conversation
 * itself is on the server, which answers 404 for one that is gone or belongs
 * to another user — and then the dock just starts fresh.
 */
const CURRENT_KEY = 'assistant-dock:conversation';

function readCurrent(): string | null {
  try {
    return window.localStorage.getItem(CURRENT_KEY);
  } catch {
    return null;
  }
}

function writeCurrent(id: string | null): void {
  try {
    if (id === null) window.localStorage.removeItem(CURRENT_KEY);
    else window.localStorage.setItem(CURRENT_KEY, id);
  } catch {
    /* storage unavailable: the conversation just isn't reopened on reload */
  }
}

/** A stored conversation in the shape the transcript renders. */
function fromDetail(detail: ConversationDetail, conversationsUrl: string): ChatMessage[] {
  const messages: ChatMessage[] = detail.messages.map((m) => ({
    id: `s${m.id}`,
    serverId: m.id,
    role: m.role,
    content: m.content,
    ...(m.attachments.length > 0
      ? {
          attachments: m.attachments.map(
            (a): ChatAttachment => ({
              name: a.name,
              mimeType: a.mime_type,
              src: attachmentUrl(conversationsUrl, detail.id, m.id, a.index),
            }),
          ),
        }
      : {}),
    ...(m.role === 'assistant'
      ? {
          requestId: m.request_id,
          truncated: m.finish_reason === 'length',
          feedback: m.feedback ?? null,
        }
      : {}),
  }));

  // Completion is recorded on the conversation; the transcript shows it on
  // the reply that did it, which is the last one.
  if (detail.completed) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') {
        messages[i] = { ...messages[i], completed: true, document: detail.document };
        break;
      }
    }
  }

  return messages;
}

interface UseChatOptions {
  chatUrl: string;
  conversationsUrl: string;
  csrfToken: string;
  stream: boolean;
  /** Server-side cap on a whole conversation. */
  maxMessages: number;
  maxMessageChars: number;
  maxAttachmentsPerMessage: number;
}

export function useChat({
  chatUrl,
  conversationsUrl,
  csrfToken,
  stream,
  maxMessages,
  maxMessageChars,
  maxAttachmentsPerMessage,
}: UseChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  /** The server's id for this conversation; null until its first turn is stored. */
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  /** Tools used by the turn currently streaming; moved onto the message at the end. */
  const [pendingTools, setPendingTools] = useState<ToolCall[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  /**
   * The conversation has reached its end — the API reported that a turn did
   * the thing the user came for. Nothing more can be sent; `reset` starts
   * over. Derived from the transcript rather than kept as its own state so it
   * cannot drift from what the messages say.
   */
  const completed = messages.some((m) => m.role === 'assistant' && m.completed === true);
  /**
   * No room for another message and its reply. The server refuses the turn
   * too; this only closes the composer before the user types into it.
   */
  const full = messages.length + 2 > maxMessages;
  /**
   * The transcript ends on a question with no reply and nothing in flight —
   * a turn that failed or was cut off, most visibly after a reload, where the
   * error that explained it is gone but the stored message is not. Retry
   * answers it.
   */
  const unanswered =
    !busy &&
    !loading &&
    pending === null &&
    messages.length > 0 &&
    messages[messages.length - 1].role === 'user';

  const abortRef = useRef<AbortController | null>(null);
  // Mirrors `pending` for the stop handler: state is stale inside the async
  // closure, but a ref always holds what has actually arrived.
  const pendingRef = useRef('');
  const toolsRef = useRef<ToolCall[]>([]);
  // Read by the async turn, which must see an id that arrived mid-stream.
  const conversationRef = useRef<string | null>(null);

  const adopt = useCallback((id: string | null) => {
    conversationRef.current = id;
    setConversationId(id);
    writeCurrent(id);
  }, []);

  const finish = useCallback(
    (meta: TurnMeta, stopped: boolean) => {
      const text = pendingRef.current;
      const tools = toolsRef.current;
      pendingRef.current = '';
      toolsRef.current = [];
      setPending(null);
      setPendingTools([]);
      setBusy(false);
      abortRef.current = null;

      // A turn that ran tools but produced no text still happened — keeping it
      // shows what the assistant did instead of silently dropping the turn.
      // A completed turn is always kept: the order was saved even if the
      // model said nothing about it, and the lock must not be lost.
      if (text === '' && tools.length === 0 && !meta.completed) return;

      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          ...(meta.messageId !== null ? { serverId: meta.messageId } : {}),
          role: 'assistant',
          content: text,
          ...(tools.length > 0 ? { tools } : {}),
          requestId: meta.requestId,
          usage: meta.usage,
          truncated: meta.truncated,
          stopped,
          ...(meta.completed ? { completed: true, document: meta.document } : {}),
        },
      ]);
    },
    [],
  );

  const run = useCallback(
    async (turn: Omit<TurnRequest, 'conversation_id'>) => {
      const controller = new AbortController();
      abortRef.current = controller;
      pendingRef.current = '';
      toolsRef.current = [];
      setPending('');
      setPendingTools([]);
      setBusy(true);
      setError(null);

      const request: TurnRequest = { conversation_id: conversationRef.current, ...turn };

      // Tools and sub-agent hand-offs share one list, in the order they ran.
      const startStep = (kind: ToolCall['kind']) => (call: StepEvent) => {
        // A ref already present means a duplicate start — keep the first, so
        // the step doesn't flicker or double up.
        if (toolsRef.current.some((t) => t.ref === call.ref)) return;
        // An agent runs its tools and sub-agents between its own start and
        // end, so a step starting while one is open belongs to the innermost.
        const parent = toolsRef.current
          .filter((t) => t.kind === 'agent' && !t.done)
          .pop()?.ref;
        toolsRef.current = [
          ...toolsRef.current,
          { ...call, kind, ...(parent !== undefined ? { parent } : {}), done: false },
        ];
        setPendingTools(toolsRef.current);
      };
      const endStep = (end: StepEnd) => {
        toolsRef.current = toolsRef.current.map((t) =>
          t.ref === end.ref ? { ...t, done: true, ...(end.label ? { label: end.label } : {}) } : t,
        );
        setPendingTools(toolsRef.current);
      };

      try {
        if (stream) {
          await streamChat(
            chatUrl,
            csrfToken,
            request,
            {
              onConversation: adopt,
              onDelta: (text) => {
                pendingRef.current += text;
                setPending(pendingRef.current);
              },
              onDone: (meta) => finish(meta, false),
              onToolStart: startStep('tool'),
              onToolEnd: endStep,
              onAgentStart: startStep('agent'),
              onAgentEnd: endStep,
            },
            controller.signal,
          );
        } else {
          const result = await sendChat(chatUrl, csrfToken, request, controller.signal);
          if (result.conversationId) adopt(result.conversationId);
          pendingRef.current = result.text;
          finish(result, false);
        }
      } catch (e: unknown) {
        if (controller.signal.aborted) {
          // User pressed stop. Keep whatever had already arrived on screen;
          // the server keeps only the question, so Retry asks it again.
          finish(
            {
              messageId: null,
              requestId: null,
              usage: null,
              truncated: false,
              completed: false,
              document: null,
            },
            true,
          );
          return;
        }

        if (e instanceof ChatError && e.conversationId) adopt(e.conversationId);

        pendingRef.current = '';
        toolsRef.current = [];
        setPending(null);
        setPendingTools([]);
        setBusy(false);
        abortRef.current = null;
        setError({
          message: e instanceof Error ? e.message : 'The assistant failed.',
          requestId: e instanceof ChatError ? e.requestId : null,
        });
      }
    },
    [adopt, chatUrl, csrfToken, finish, stream],
  );

  const send = useCallback(
    (text: string, attachments: PendingAttachment[] = []) => {
      const content = text.trim();
      if (content === '' && attachments.length === 0) return;
      if (busy || completed || loading) return;

      if (content.length > maxMessageChars) {
        setError({
          message: `That message is ${content.length.toLocaleString()} characters. The limit is ${maxMessageChars.toLocaleString()}.`,
          requestId: null,
        });
        return;
      }

      if (attachments.length > maxAttachmentsPerMessage) {
        setError({
          message: `That's ${attachments.length} attachments. The limit is ${maxAttachmentsPerMessage} per message.`,
          requestId: null,
        });
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'user',
          content,
          ...(attachments.length > 0
            ? {
                attachments: attachments.map((a) => ({
                  name: a.name,
                  mimeType: a.mimeType,
                  src: a.dataUrl,
                })),
              }
            : {}),
        },
      ]);
      void run({
        content,
        ...(attachments.length > 0
          ? { attachments: attachments.map((a) => ({ name: a.name, data: a.dataUrl })) }
          : {}),
      });
    },
    [busy, completed, loading, maxAttachmentsPerMessage, maxMessageChars, run],
  );

  /** Drop the last assistant turn and ask again from the same user message. */
  const retry = useCallback(() => {
    // Retrying a completed turn would save the order a second time.
    if (busy || completed || loading) return;

    let cut = messages.length;
    while (cut > 0 && messages[cut - 1].role === 'assistant') cut -= 1;
    if (cut === 0) return;

    const last = messages[cut - 1];
    setMessages(messages.slice(0, cut));

    if (conversationRef.current !== null) {
      void run({ retry: true });
      return;
    }

    // The first turn never reached the server, so there is nothing stored to
    // retry: send the message again. A file attached in this page still has
    // its data URL.
    const files = (last.attachments ?? []).filter((a) => a.src.startsWith('data:'));
    void run({
      content: last.content,
      ...(files.length > 0 ? { attachments: files.map((a) => ({ name: a.name, data: a.src })) } : {}),
    });
  }, [busy, completed, loading, messages, run]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    pendingRef.current = '';
    toolsRef.current = [];
    setMessages([]);
    setPending(null);
    setPendingTools([]);
    setBusy(false);
    setError(null);
  }, []);

  /** Start a new conversation; the server creates it with the first message. */
  const reset = useCallback(() => {
    clear();
    adopt(null);
  }, [adopt, clear]);

  /** Reopen a stored conversation. */
  const open = useCallback(
    async (id: string) => {
      clear();
      setLoading(true);
      try {
        const detail = await getConversation(conversationsUrl, id);
        setMessages(fromDetail(detail, conversationsUrl));
        adopt(detail.id);
      } catch (e: unknown) {
        adopt(null);
        setError({
          message: e instanceof Error ? e.message : 'Could not open that conversation.',
          requestId: null,
        });
      } finally {
        setLoading(false);
      }
    },
    [adopt, clear, conversationsUrl],
  );

  // Reopen the conversation that was open before a reload — unless it has
  // since finished, in which case a fresh one is what the user wants.
  useEffect(() => {
    const id = readCurrent();
    if (id === null || conversationsUrl === '') return;

    let cancelled = false;
    setLoading(true);
    getConversation(conversationsUrl, id)
      .then((detail) => {
        if (cancelled) return;
        if (detail.completed) {
          adopt(null);
          return;
        }
        setMessages(fromDetail(detail, conversationsUrl));
        adopt(detail.id);
      })
      .catch(() => {
        if (!cancelled) adopt(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Runs once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissError = useCallback(() => setError(null), []);

  /**
   * Rate a stored reply, or take the rating back with 0. Shown at once and
   * put back as it was if the server refuses it.
   */
  const rate = useCallback(
    async (
      id: string,
      rating: 1 | -1 | 0,
      reasons: FeedbackReason[] = [],
      comment: string | null = null,
    ): Promise<void> => {
      const message = messages.find((m) => m.id === id);
      const conversation = conversationRef.current;
      if (!message || message.serverId === undefined || conversation === null) return;

      const previous = message.feedback ?? null;
      const optimistic: MessageFeedback | null =
        rating === 0 ? null : { rating, reasons: rating === -1 ? reasons : [], comment: rating === -1 ? comment : null };
      const setFeedback = (feedback: MessageFeedback | null) =>
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, feedback } : m)));

      setFeedback(optimistic);
      try {
        setFeedback(
          await sendFeedback(conversationsUrl, csrfToken, conversation, message.serverId, rating, reasons, comment),
        );
      } catch {
        setFeedback(previous);
      }
    },
    [conversationsUrl, csrfToken, messages],
  );

  return {
    messages,
    conversationId,
    loading,
    pending,
    pendingTools,
    busy,
    completed,
    full,
    unanswered,
    error,
    send,
    retry,
    stop,
    reset,
    open,
    dismissError,
    rate,
  };
}
